import L from 'leaflet';
import { getDynmapBase } from './config';
import type { HdProjection } from './dynmapCrs';

const DYNMAP_ORIGIN = 'https://map.reborned.one';
/** Bump when CF edge holds poisoned 502 for bare /tiles URLs. */
const TILE_CACHE_EPOCH = 'rb2';

/**
 * Dynmap HDMapType tile layer (LiveAtlas getTileInfo / getTileName).
 *
 * Reliability:
 * - Prefer same-origin /tiles (nginx disk cache) on map2/map3.
 * - Append ?v=epoch so Cloudflare cannot serve stale 502 from an older
 *   Cache-Control:always misconfig.
 * - On error: cache-bust, then fall back to map.reborned.one directly.
 * - Never set crossOrigin on <img> (breaks non-CORS hosts).
 */
export function createDynmapTileLayer(
  worldName: string,
  proj: HdProjection,
  baseUrl: string = getDynmapBase(),
): L.TileLayer {
  const tileSize = proj.tileSize;
  const mapzoomin = proj.mapzoomin;
  const mapzoomout = proj.mapzoomout;
  const maxZoom = mapzoomin + mapzoomout;
  const maxNativeZoom = mapzoomout;
  const prefix = proj.prefix;
  const fmt = proj.imageFormat;
  const primaryRoot = `${baseUrl.replace(/\/$/, '')}/tiles/${worldName}/`;
  const fallbackRoot = `${DYNMAP_ORIGIN}/tiles/${worldName}/`;
  const useEpoch =
    typeof window !== 'undefined' &&
    /^(map2|map3)\./i.test(window.location.hostname);

  const layer = L.tileLayer(`${primaryRoot}${prefix}/{z}/{x}/{y}.${fmt}`, {
    tileSize,
    minZoom: 0,
    maxZoom,
    maxNativeZoom,
    zoomReverse: true,
    noWrap: true,
    updateWhenIdle: false,
    updateWhenZooming: false,
    keepBuffer: 2,
    updateInterval: 50,
    opacity: 1,
    attribution: 'ReBorned · Dynmap tiles',
    className: 'dynmap-tile',
  });

  const zoomprefix = (amount: number): string =>
    'z'.repeat(amount) + (amount === 0 ? '' : '_');

  function tilePath(coords: L.Coords, izoom: number): string {
    const zoomoutlevel = Math.max(0, izoom - mapzoomin);
    const scale = 1 << zoomoutlevel;
    let x = scale * coords.x;
    let y = scale * coords.y;
    y = -y;
    const scaledx = x >> 5;
    const scaledy = y >> 5;
    const zoom = zoomprefix(zoomoutlevel);
    return `${prefix}/${scaledx}_${scaledy}/${zoom}${x}_${y}.${fmt}`;
  }

  (layer as L.TileLayer & { getTileUrl: (c: L.Coords) => string }).getTileUrl =
    function getTileUrl(this: L.TileLayer, coords: L.Coords): string {
      const self = this as L.TileLayer & { _getZoomForUrl?: () => number };
      const izoom =
        typeof self._getZoomForUrl === 'function'
          ? self._getZoomForUrl()
          : maxZoom - coords.z;
      const path = tilePath(coords, izoom);
      const url = `${primaryRoot}${path}`;
      return useEpoch ? `${url}?v=${TILE_CACHE_EPOCH}` : url;
    };

  type Attempt = { n: number; stage: 'primary' | 'bust' | 'direct' };
  const attempts = new WeakMap<HTMLElement, Attempt>();

  layer.on('tileerror', (e: L.TileErrorEvent) => {
    const img = e.tile as HTMLImageElement | undefined;
    if (!img) return;

    const state = attempts.get(img) ?? { n: 0, stage: 'primary' as const };
    state.n += 1;
    attempts.set(img, state);

    const self = layer as L.TileLayer & { _getZoomForUrl?: () => number };
    const izoom =
      typeof self._getZoomForUrl === 'function'
        ? self._getZoomForUrl()
        : maxZoom - e.coords.z;
    const path = tilePath(e.coords, izoom);

    // 1–2: same-origin with cache-bust (bypass CF stale 502)
    if (state.n <= 2) {
      state.stage = 'bust';
      const url = `${primaryRoot}${path}${path.includes('?') ? '&' : '?'}_r=${state.n}&_t=${Date.now()}`;
      window.setTimeout(() => {
        img.src = url;
      }, 80 * state.n);
      return;
    }

    // 3–4: direct Dynmap origin (img tags don't need CORS)
    if (state.n <= 4) {
      state.stage = 'direct';
      const url = `${fallbackRoot}${path}${path.includes('?') ? '&' : '?'}_d=${state.n}`;
      window.setTimeout(() => {
        img.src = url;
      }, 60);
      return;
    }
    // give up — leave void (unexplored or truly missing)
  });

  // If primary base is already dynmap, skip dual logic noise
  if (baseUrl.replace(/\/$/, '') === DYNMAP_ORIGIN) {
    layer.off('tileerror');
    // simpler retry only
    layer.on('tileerror', (e: L.TileErrorEvent) => {
      const img = e.tile as HTMLImageElement | undefined;
      if (!img) return;
      const state = attempts.get(img) ?? { n: 0, stage: 'primary' as const };
      state.n += 1;
      if (state.n > 3) return;
      attempts.set(img, state);
      const self = layer as L.TileLayer & { _getZoomForUrl?: () => number };
      const izoom =
        typeof self._getZoomForUrl === 'function'
          ? self._getZoomForUrl()
          : maxZoom - e.coords.z;
      const path = tilePath(e.coords, izoom);
      window.setTimeout(() => {
        img.src = `${fallbackRoot}${path}?_r=${state.n}`;
      }, 100 * state.n);
    });
  }

  return layer;
}
